// Supabase client wired to Clerk via the third-party-auth pattern.
//
// The `accessToken` callback runs before every Supabase request and returns a
// fresh Clerk-issued JWT (template = "supabase"). Supabase TPA validates it
// against the Clerk JWKS we registered, and current_user_uuid() extracts the
// supabase_uuid claim for RLS.
//
// Why NOT setSession({access_token, refresh_token: ""})?
//   - supabase-js v2 rejects empty refresh tokens with AuthSessionMissingError.
//   - setSession assumes Supabase-Auth-issued JWT pairs (access + refresh).
//   - Clerk doesn't issue Supabase-style refresh tokens.
// The accessToken callback was added in supabase-js v2.45 specifically for this
// third-party-JWT use case. Auto-fresh on every call, no manual refresh loops.
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
  console.error(
    "[Supabase] Missing env vars — VITE_SUPABASE_URL or VITE_SUPABASE_PUBLISHABLE_KEY not set. " +
    "Database features will be unavailable."
  );
}

export const supabase = createClient<Database>(
  SUPABASE_URL || "https://placeholder.supabase.co",
  SUPABASE_PUBLISHABLE_KEY || "placeholder",
  {
    // Clerk → Supabase bridge: every Supabase REST/Realtime call pulls a fresh
    // JWT from window.Clerk.session.getToken({template:"supabase"}). Returns
    // null when the user is signed out → Supabase treats the call as anon.
    accessToken: async () => {
      try {
        const clerk = (typeof window !== "undefined" && (window as any).Clerk) || null;
        if (!clerk?.session) return null;
        // skipCache: true forces Clerk to re-render the JWT template from the
        // LIVE user record, not the session's cached state. We need this
        // because public_metadata.supabase_uuid gets PATCHed mid-session by
        // clerk-link on first sign-in and the cached state misses it.
        return await clerk.session.getToken({ template: "supabase", skipCache: true });
      } catch (e) {
        console.warn("[supabase] accessToken callback failed:", e);
        return null;
      }
    },
    auth: {
      // Disable supabase-auth-managed session storage — we're not the auth source.
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
    },
  }
);
